import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl, { Map as MlMap, GeoJSONSource } from 'maplibre-gl';
import type { Feature } from 'geojson';
import type { LngLat } from '../lib/types';
import type { Fix } from '../lib/gps';
import {
  formatKm,
  loadDirectory,
  searchDirectory,
  STATE_NAMES,
  type Directory,
  type SearchHit,
} from '../lib/directory';
import { basemapStyle } from '../lib/basemap';
import { emptyFc, fc } from '../lib/shapes';

interface Props {
  fix: Fix | null;
  activeCourseId: string;
  onPick: (hit: SearchHit) => void;
}

const AU_CENTRE: LngLat = [134.5, -25.7];

/**
 * Every golf course in Australia, on one map.
 *
 * Rendering 1000+ pins on a cheap head-unit GPU needs care: the markers are a
 * single GeoJSON source with clustering, not 1000 DOM elements. That is the
 * difference between a map that pans smoothly and one that locks the screen.
 */
export default function CoursesScreen({ fix, activeCourseId, onPick }: Props) {
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<MlMap | null>(null);
  const ready = useRef(false);
  const [dir, setDir] = useState<Directory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [state, setState] = useState('');
  const [nearMe, setNearMe] = useState(true);

  useEffect(() => {
    loadDirectory()
      .then(setDir)
      .catch((e) => setError((e as Error).message));
  }, []);

  const hits = useMemo(() => {
    if (!dir) return [];
    return searchDirectory(dir, {
      query,
      state,
      near: nearMe && fix ? fix.pos : null,
      limit: 300,
    });
  }, [dir, query, state, nearMe, fix]);

  // --- map ---------------------------------------------------------------
  useEffect(() => {
    if (!el.current || map.current) return;
    const m = new maplibregl.Map({
      container: el.current,
      style: basemapStyle(),
      center: fix?.pos ?? AU_CENTRE,
      zoom: fix ? 9 : 3.4,
      attributionControl: { compact: true },
    });
    map.current = m;

    m.on('load', () => {
      m.addSource('courses', {
        type: 'geojson',
        data: emptyFc,
        cluster: true,
        clusterRadius: 46,
        clusterMaxZoom: 10,
      });

      m.addLayer({
        id: 'cluster',
        type: 'circle',
        source: 'courses',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': '#1f7a3f',
          'circle-opacity': 0.85,
          'circle-stroke-color': '#38e07b',
          'circle-stroke-width': 2,
          'circle-radius': ['step', ['get', 'point_count'], 16, 10, 22, 50, 30, 200, 38],
        },
      });
      m.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'courses',
        filter: ['has', 'point_count'],
        layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-size': 14 },
        paint: { 'text-color': '#eaf3ec' },
      });
      m.addLayer({
        id: 'course-dot',
        type: 'circle',
        source: 'courses',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-radius': ['case', ['get', 'active'], 11, 7],
          'circle-color': ['case', ['get', 'active'], '#ffd23f', '#38e07b'],
          'circle-stroke-color': '#0b120d',
          'circle-stroke-width': 2,
        },
      });
      m.addLayer({
        id: 'course-label',
        type: 'symbol',
        source: 'courses',
        filter: ['!', ['has', 'point_count']],
        minzoom: 9,
        layout: {
          'text-field': ['get', 'name'],
          'text-size': 12,
          'text-offset': [0, 1.3],
          'text-allow-overlap': false,
        },
        paint: { 'text-color': '#eaf3ec', 'text-halo-color': '#0b120d', 'text-halo-width': 1.6 },
      });

      m.on('click', 'cluster', (e) => {
        const f = m.queryRenderedFeatures(e.point, { layers: ['cluster'] })[0];
        const id = f?.properties?.cluster_id;
        if (id == null) return;
        (m.getSource('courses') as GeoJSONSource)
          .getClusterExpansionZoom(id as number)
          .then((z) => {
            const g = f.geometry;
            if (g.type === 'Point') m.easeTo({ center: g.coordinates as [number, number], zoom: z });
          })
          .catch(() => {});
      });

      m.on('click', 'course-dot', (e) => {
        const p = e.features?.[0]?.properties as Record<string, string> | undefined;
        if (!p) return;
        onPickRef.current({
          id: p.id,
          name: p.name,
          lat: Number(p.lat),
          lng: Number(p.lng),
          state: p.state,
          holes: p.holes ? Number(p.holes) : null,
          distanceM: null,
        });
      });
      for (const layer of ['cluster', 'course-dot']) {
        m.on('mouseenter', layer, () => (m.getCanvas().style.cursor = 'pointer'));
        m.on('mouseleave', layer, () => (m.getCanvas().style.cursor = ''));
      }

      ready.current = true;
      m.fire('fairway:dir-ready');
    });

    return () => {
      ready.current = false;
      m.remove();
      map.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;

  useEffect(() => {
    const m = map.current;
    if (!m || !dir) return;
    const paint = () => {
      (m.getSource('courses') as GeoJSONSource | undefined)?.setData(
        fc(
          hits.map<Feature>((c) => ({
            type: 'Feature',
            properties: {
              id: c.id,
              name: c.name,
              lat: c.lat,
              lng: c.lng,
              state: c.state,
              holes: c.holes ?? '',
              active: c.id === activeCourseId,
            },
            geometry: { type: 'Point', coordinates: [c.lng, c.lat] },
          }))
        )
      );
    };
    if (ready.current) paint();
    else m.once('fairway:dir-ready', paint);
  }, [hits, dir, activeCourseId]);

  const flyTo = (c: SearchHit) => {
    map.current?.easeTo({ center: [c.lng, c.lat], zoom: 14, duration: 800 });
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(330px, 36%) 1fr', height: '100%' }}>
      <div className="screen" style={{ borderRight: '1px solid var(--line)' }}>
        <div className="card">
          <h2>
            AUSTRALIAN COURSES
            {dir ? ` · ${dir.count.toLocaleString()}` : ''}
          </h2>
          <div className="field">
            <input
              value={query}
              placeholder="Search by name or suburb"
              onChange={(e) => setQuery(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="row">
            <select
              className="btn"
              style={{ background: 'var(--panel-2)' }}
              value={state}
              onChange={(e) => setState(e.target.value)}
            >
              <option value="">All states</option>
              {dir &&
                Object.keys(dir.byState)
                  .sort()
                  .map((s) => (
                    <option key={s} value={s}>
                      {STATE_NAMES[s] ?? s} ({dir.byState[s]})
                    </option>
                  ))}
            </select>
            <button
              className={`btn ${nearMe ? 'primary' : 'ghost'}`}
              onClick={() => setNearMe((v) => !v)}
              disabled={!fix}
            >
              {fix ? 'Nearest first' : 'No GPS'}
            </button>
          </div>
          {error && <div className="muted" style={{ color: 'var(--danger)' }}>{error}</div>}
          {!dir && !error && <div className="muted">Loading directory…</div>}
        </div>

        <div className="card">
          <h2>{hits.length} SHOWN</h2>
          <table className="grid">
            <tbody>
              {hits.slice(0, 60).map((c) => (
                <tr
                  key={c.id}
                  onClick={() => flyTo(c)}
                  style={{ cursor: 'pointer', background: c.id === activeCourseId ? 'rgba(56,224,123,.14)' : undefined }}
                >
                  <td className="name">
                    {c.name}
                    <div className="muted">
                      {[
                        c.suburb,
                        STATE_NAMES[c.state] ?? c.state,
                        c.holes ? `${c.holes} holes` : c.par ? `par ${c.par}` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </td>
                  <td>{formatKm(c.distanceM)}</td>
                  <td>
                    <button
                      className="btn primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPick(c);
                      }}
                    >
                      Play
                    </button>
                  </td>
                </tr>
              ))}
              {dir && !hits.length && (
                <tr>
                  <td className="muted">No course matches that search.</td>
                </tr>
              )}
            </tbody>
          </table>
          {hits.length > 60 && (
            <div className="muted" style={{ marginTop: 8 }}>
              Showing the closest 60 of {hits.length}. Keep typing to narrow it.
            </div>
          )}
        </div>

        {dir && (
          <div className="card">
            <div className="muted">
              Course directory: {dir.source}. Selecting a course gives you its location and card;
              per-hole yardages need a survey pass on that course.
            </div>
          </div>
        )}
      </div>

      <div style={{ position: 'relative' }}>
        <div ref={el} className="map" />
      </div>
    </div>
  );
}
